/**
 * Simple Bundles – Infinite Options Image Swap v2 (17 Feb 2026)
 *
 * Swaps the main PDP image when a customer changes an Infinite Options
 * bundle dropdown. Uses the bundled_variants metafield for reliable
 * product/variant → image mapping. Should work acrosss any Shopify theme.
 *
 * ─────────────────────────────────────────────────────────────────────
 * INSTALLATION (pick one):
 *
 *   Option A – Theme asset (recommended):
 *     1. Upload this file to your theme's assets/ folder
 *     2. Add before </body> in layout/theme.liquid:
 *        <script src="{{ 'sb-image-swap.js' | asset_url }}" defer></script>
 *
 *   Option B – Inline snippet:
 *     Wrap in <script>…</script> at the bottom of your product template
 *
 * ─────────────────────────────────────────────────────────────────────
 * HOW IT WORKS:
 *
 *   1. On page load, fetches the current product's metafields via
 *      /products/{handle}.json to get the bundled_variants data
 *   2. Builds a lookup map: option display text → { handle, variant_id }
 *   3. Fetches each child product once (cached) to get all variant images
 *   4. On dropdown change, looks up the selection → swaps the main image
 *
 *   Because it uses the actual metafield data (handles + variant IDs),
 *   it never has to guess product handles from display text.
 * ─────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ========================================================================
  // CONFIG
  // ========================================================================
  const CONFIG = {
    // Selectors to find the main product image – tried in order. Most themes use these selectors .
    // Add your theme's selector at the top if none of these match.
    imageSelectors: [
      '.product__media-item--variant img',
      '.product__media img.image-magnify-full-size',
      '.product__media-list .product__media img',
      '.product__media img',
      '.product-image-main img',
      '.product__photos img',
      '.product__photo img.photoswipe__image',
      '.product__photo img',
      '.product-gallery__image img',
      '.product__main-photos img',
      '.product-main-slide .image-wrap img',
      '.product-featured-img',
      '.product-single__photo img',
      '.product-single__media img',
      '.product__media-item img',
      '[data-product-media-type="image"] img',
      '.product-single__image img',
      '.product__image-wrapper img',
    ],

    ioBundleContainer: '#simple-bundles-io-options',
    transitionMs: 200,
  };

  // ========================================================================
  // STATE
  // ========================================================================
  let mainImageEl = null;
  let originalSrc = null;
  let originalSrcset = null;

  // Map: normalized option text → image URL
  // e.g. "cotton canvas cap" → "https://cdn.shopify.com/..."
  const imageMap = {};

  // Cache for /products/{handle}.js fetches
  const productCache = {};

  /*
   * Keep the exact bundled_variants payload that init() successfully loads.
   * The normal image previews already use this data. The climbing-belt
   * resolver must use the same payload instead of depending on a separate
   * window global that may not exist on this product page.
   */
  let loadedBundledVariants = null;

  // ========================================================================
  // HELPERS
  // ========================================================================

  /**
   * Normalize option text for reliable matching.
   * Strips price suffixes like "(+CA$5.00)", whitespace, and lowercases.
   */
  function normalizeOptionText(text) {
    return text
      .replace(/\([\+\-]?\s*[A-Z]{0,3}\$?\s*[\d,.]+\)/g, '') // (+CA$5.00), (-$3.00), etc.
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  /*
   * Extracts the D-ring size used by the climbing belt dropdown.
   * Example: "Extra Large D29-33" -> "d29-33"
   */
  function getClimbingBeltSizeKey(text) {
    if (!text) return '';

    const match = String(text).match(
      /\bd\s*(\d{2})\s*[–—-]\s*(\d{2})\b/i
    );

    return match
      ? `d${match[1]}-${match[2]}`
      : '';
  }

  /**
   * Find the main product image on the page.
   * Tries configured selectors, then falls back to largest image in product area.
   */
  function findMainImage() {
    for (const sel of CONFIG.imageSelectors) {
      const img = document.querySelector(sel);
      if (img && img.src) return img;
    }

    // Fallback: largest image in the product section
    const section =
      document.querySelector('.product') ||
      document.querySelector('[data-section-type="product"]') ||
      document.querySelector('.product-single') ||
      document.querySelector('main');

    if (!section) return null;

    let best = null;
    let bestArea = 0;
    section.querySelectorAll('img').forEach((img) => {
      const area = img.offsetWidth * img.offsetHeight;
      if (area > bestArea && !img.closest(CONFIG.ioBundleContainer)) {
        best = img;
        bestArea = area;
      }
    });
    return best;
  }

  /**
   * Get the current product handle from the URL.
   */
  function getProductHandle() {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  /**
   * Fetch product JSON (cached).
   */
  async function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();
      productCache[handle] = data;
      return data;
    } catch (e) {
      console.warn('[SB-IMG] fetch failed for', handle, e);
      return null;
    }
  }

  /**
   * Resize a Shopify CDN image URL to a given width.
   */
  function resizeImage(url, width) {
    if (!url) return url;
    // Remove any existing size suffix and add desired one
    return url
      .replace(/_(\d+x\d*|\d*x\d+)\./, '.')
      .replace(/\.(\w+)(\?.*)?$/, `_${width}x.$1$2`);
  }

  /**
   * Swap the main image with a fade transition.
   */
  function swapImage(newSrc) {
    if (!mainImageEl || !newSrc) return;

    // Avoid swapping to the same image
    const normCurrent = mainImageEl.src.replace(/[?#].*$/, '').replace(/_\d+x\d*\./, '.');
    const normNew = newSrc.replace(/[?#].*$/, '').replace(/_\d+x\d*\./, '.');
    if (normCurrent === normNew) return;

    mainImageEl.style.transition = `opacity ${CONFIG.transitionMs}ms ease`;
    mainImageEl.style.opacity = '0';

    setTimeout(() => {
      // Remove srcset so our src takes priority
      mainImageEl.removeAttribute('srcset');

      // Update parent <source> elements if inside a <picture>
      const picture = mainImageEl.closest('picture');
      if (picture) {
        picture.querySelectorAll('source').forEach((s) => s.remove());
      }

      // Update zoom / lightbox wrappers
      const zoomEl =
        mainImageEl.closest('[data-zoom]') ||
        mainImageEl.closest('[data-bgset]') ||
        mainImageEl.closest('[data-image]');
      if (zoomEl) {
        zoomEl.dataset.zoom && (zoomEl.dataset.zoom = resizeImage(newSrc, 2048));
        zoomEl.dataset.bgset && (zoomEl.dataset.bgset = newSrc);
      }

      mainImageEl.src = newSrc;

      const show = () => { mainImageEl.style.opacity = '1'; };
      mainImageEl.addEventListener('load', show, { once: true });
      // If cached / already loaded
      if (mainImageEl.complete) show();
    }, CONFIG.transitionMs);
  }

  function restoreOriginal() {
    if (!mainImageEl || !originalSrc) return;
    mainImageEl.style.transition = `opacity ${CONFIG.transitionMs}ms ease`;
    mainImageEl.style.opacity = '0';
    setTimeout(() => {
      mainImageEl.src = originalSrc;
      if (originalSrcset) mainImageEl.setAttribute('srcset', originalSrcset);
      mainImageEl.style.opacity = '1';
    }, CONFIG.transitionMs);
  }

  // ========================================================================
  // CORE: Build the image lookup map from bundled_variants metafield
  // ========================================================================

  /**
   * Parse the bundled_variants metafield and fetch images for each
   * child product/variant. Populates the global `imageMap`.
   *
   * bundled_variants structure (from SB metafields):
   * [
   *   {
   *     "variant_title": "Default Title",
   *     "variant_id": 123456,
   *     "handle": "sample_product_handle",
   *     "product_title": "Sample Product",
   *     "variant_options": [{ "name": "Option 1", "value": "Sample Product" }]
   *   },
   *   ...
   * ]
   *
   * The dropdown display text matches variant_options[].value
   * (e.g. "Sample Product", "Custom Water Bottle - Cornflower / Dark Berry")
   */
  async function buildImageMap(bundledVariants) {
    if (!Array.isArray(bundledVariants) || bundledVariants.length === 0) {
      console.warn('[SB-IMG] No bundled_variants data found.');
      return;
    }

    // Group by product handle to minimize fetches
    const handleGroups = {};
    for (const bv of bundledVariants) {
      if (!bv.handle) continue;
      if (!handleGroups[bv.handle]) handleGroups[bv.handle] = [];
      handleGroups[bv.handle].push(bv);
    }

    // Fetch each unique child product
    const fetches = Object.keys(handleGroups).map(async (handle) => {
      const product = await fetchProduct(handle);
      if (!product) {
        console.warn('[SB-IMG] Could not fetch product:', handle);
        return;
      }

      for (const bv of handleGroups[handle]) {
        let imageSrc = null;

        // 1. Try to find variant-specific image by variant_id
        const matchedVariant = product.variants.find(
          (v) => v.id === bv.variant_id
        );
        if (matchedVariant && matchedVariant.featured_image) {
          imageSrc = matchedVariant.featured_image.src;
        }

        // 2. Fall back to product featured image
        if (!imageSrc && product.featured_image) {
          imageSrc = product.featured_image;
        }

        // 3. Fall back to first product image
        if (!imageSrc && product.images && product.images.length > 0) {
          imageSrc = product.images[0];
        }

        if (!imageSrc) {
          console.log('[SB-IMG] No image available for:', bv.product_title);
          continue;
        }

        // Request a good PDP size
        imageSrc = resizeImage(imageSrc, 1024);

        // Map all possible text keys for this variant
        const keys = new Set();

        // From variant_options (primary – matches dropdown display text)
        if (bv.variant_options) {
          for (const opt of bv.variant_options) {
            if (opt.value) keys.add(normalizeOptionText(opt.value));
          }
        }

        // From product_title + variant_title combo
        if (bv.product_title) {
          keys.add(normalizeOptionText(bv.product_title));
          if (bv.variant_title && bv.variant_title !== 'Default Title') {
            keys.add(
              normalizeOptionText(
                `${bv.product_title} - ${bv.variant_title}`
              )
            );
          }
        }

        for (const key of keys) {
          imageMap[key] = imageSrc;
        }
      }
    });

    await Promise.all(fetches);
    console.log('[SB-IMG] Image map built:', Object.keys(imageMap).length, 'entries', imageMap);
  }

  /**
   * Get bundled_variants data. Tries multiple sources:
   *   1. Fetch from /products/{handle}.json (most reliable, works on any theme)
   *   2. Parse from page JSON-LD / script tags (fallback)
   */
  async function getBundledVariants() {
    const handle = getProductHandle();
    if (!handle) return null;

    // Fetch the full product JSON which includes metafields on some themes
    // But Shopify's /products/{handle}.json doesn't include metafields,
    // so we use the Storefront-compatible metafield endpoint instead.
    try {
      const res = await fetch(
        `/products/${handle}.json`
      );
      if (res.ok) {
        const data = await res.json();
        const product = data.product || data;

        // Check if metafields are embedded (some themes do this)
        if (product.metafields) {
          const sbMeta =
            product.metafields.simple_bundles ||
            product.metafields['app--4901177--simple_bundles'];
          if (sbMeta && sbMeta.bundled_variants) {
            return JSON.parse(sbMeta.bundled_variants);
          }
        }
      }
    } catch (e) {
      // Continue to next method
    }

    // Method 2: Look for metafield data in <script type="application/json"> tags
    const scripts = document.querySelectorAll('script[type="application/json"]');
    for (const script of scripts) {
      try {
        const data = JSON.parse(script.textContent);
        // Look for bundled_variants in various structures
        const searchObj = (obj, depth = 0) => {
          if (depth > 5 || !obj || typeof obj !== 'object') return null;
          if (obj.bundled_variants) {
            const val = obj.bundled_variants;
            return typeof val === 'string' ? JSON.parse(val) : val;
          }
          for (const key in obj) {
            const result = searchObj(obj[key], depth + 1);
            if (result) return result;
          }
          return null;
        };
        const found = searchObj(data);
        if (found) return found;
      } catch (e) {
        // Not the right JSON block
      }
    }

    // Method 3: Use Liquid-injected global (if the merchant added our snippet)
    if (window.__SB_BUNDLED_VARIANTS__) {
      return typeof window.__SB_BUNDLED_VARIANTS__ === 'string'
        ? JSON.parse(window.__SB_BUNDLED_VARIANTS__)
        : window.__SB_BUNDLED_VARIANTS__;
    }

    return null;
  }

  // ========================================================================
  // DROPDOWN EVENT HANDLING
  // ========================================================================
function getProductUrlForSelection(normalized) {
  let bundledVariants = window.__SB_BUNDLED_VARIANTS__;

  if (!bundledVariants) {
    return '';
  }

  if (typeof bundledVariants === 'string') {
    try {
      bundledVariants = JSON.parse(bundledVariants);
    } catch (error) {
      return '';
    }
  }

  if (!Array.isArray(bundledVariants)) {
    return '';
  }

  const matchingItem = bundledVariants.find(function(item) {
    if (
      !item ||
      !item.handle ||
      !Array.isArray(item.variant_options)
    ) {
      return false;
    }

    return item.variant_options.some(function(option) {
      if (!option || !option.value) {
        return false;
      }

      const optionText =
        normalizeOptionText(option.value);

      return (
        optionText === normalized ||
        normalized.includes(optionText) ||
        optionText.includes(normalized)
      );
    });
  });

  if (!matchingItem || !matchingItem.handle) {
    return '';
  }

  const root =
    window.Shopify &&
    window.Shopify.routes &&
    window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  return root + 'products/' + matchingItem.handle;
}
function getTitanDecisionLabel(select) {
  const box =
    select.closest('.titan-decision-box');

  if (!box) {
    return '';
  }

  const label =
    box.querySelector(
      'label, strong, .label, .form__label'
    );

  return label
    ? label.textContent.trim()
    : '';
}


function isTitanPreferredSelection(
  select,
  cleanProductName
) {

  const category =
    getTitanDecisionLabel(select)
      .toLowerCase();


  /*
   * HAND TOOL KIT
   *
   * The default selection for every hand-tool
   * category is the Titan Preferred product.
   */
  const handToolCategories = [
    'lineman pliers',
    'fiberglass folding rule',
    '4-in-1 speed wrench',
    'lineman hammer',
    'skinning knife',
    'adjustable wrench',
    'heavy-duty screwdriver',
    'lineman wrench',
    'tongue-and-groove pliers',
    'hard hat',
    'safety glasses - clear',
    'safety glasses - shaded'
  ];

  if (
    handToolCategories.some(function(name) {
      return category.includes(name);
    })
  ) {
    return (
      select.value ===
      select.dataset.titanPreferredValue
    );
  }


  /*
   * PRIMARY FALL RESTRICTION
   * Default selection is Titan Preferred.
   */
  if (
    category.includes('primary fall restriction')
  ) {
    return (
      select.value ===
      select.dataset.titanPreferredValue
    );
  }


  /*
   * SECONDARY POSITIONING LANYARD
   * Default selection is Titan Preferred.
   */
  if (
    category.includes('secondary positioning lanyard')
  ) {
    return (
      select.value ===
      select.dataset.titanPreferredValue
    );
  }


  /*
   * CLIMBER KIT
   * Default selection is Titan Preferred.
   */
  if (
    category.includes('climber kit')
  ) {
    return (
      select.value ===
      select.dataset.titanPreferredValue
    );
  }


  /*
   * CLIMBING BELTS
   * ALL belt selections are Titan Preferred.
   */
  if (
    category.includes('climbing belt')
  ) {
    return true;
  }


  /*
   * TOOL HOLSTER
   * ONLY 42266S-BL is Titan Preferred.
   */
  if (
    category.includes('tool holster')
  ) {
    return /42266s-bl/i.test(
      cleanProductName
    );
  }


  /*
   * BOLT BAG
   * ONLY 4570B2 is Titan Preferred.
   */
  if (
    category.includes('bolt bag')
  ) {
    return /4570b2/i.test(
      cleanProductName
    );
  }

  /*
 * HANDLINE HOOK / CARRIER
 * ONLY Buckingham 2402GH is Titan Preferred.
 */
if (
  category.includes('handline hook') ||
  category.includes('handline carrier')
) {
  return /2402gh/i.test(
    cleanProductName
  );
}

  /*
   * WORK GLOVES
   * ALL glove selections are Titan Preferred.
   */
  if (
    category.includes('work gloves')
  ) {
    return true;
  }


  /*
   * GEAR BAG / BACKPACK
   * ONLY Buckingham 4377 is Titan Preferred.
   */
  if (
    category.includes('gear bag') ||
    category.includes('backpack')
  ) {
    return /\b4377\b/i.test(
      cleanProductName
    );
  }


  /*
   * Anything else:
   * no Titan Preferred badge.
   */
  return false;
}
/*
 * Resolve ONLY the Climbing Belt Size preview.
 *
 * This is intentionally separate from the global imageMap so a belt size
 * can never accidentally inherit an image from gloves, bags, or another
 * bundle component that happens to use similar option text.
 *
 * Any failure here affects only the belt preview; the rest of the existing
 * preview system continues unchanged.
 */
async function getClimbingBeltPreviewData(rawSelection) {
  try {
    const sizeKey =
      getClimbingBeltSizeKey(rawSelection);

    if (!sizeKey) {
      return null;
    }

    let bundledVariants =
      loadedBundledVariants ||
      window.__SB_BUNDLED_VARIANTS__;

    if (!bundledVariants) {
      console.warn(
        '[SB-IMG] Belt lookup has no bundled_variants payload.'
      );
      return null;
    }

    if (typeof bundledVariants === 'string') {
      bundledVariants =
        JSON.parse(bundledVariants);
    }

    if (!Array.isArray(bundledVariants)) {
      return null;
    }

    /*
     * First preference: the known Buckingham 20192CM belt.
     * Second preference: a belt/body-belt product with the same D-ring range.
     */
    const candidates =
      bundledVariants.filter(function(item) {
        if (!item || !item.handle) {
          return false;
        }

        const searchable = [
          item.product_title || '',
          item.handle || '',
          item.variant_title || '',
          ...(Array.isArray(item.variant_options)
            ? item.variant_options.map(function(opt) {
                return opt && opt.value
                  ? opt.value
                  : '';
              })
            : [])
        ].join(' ');

        const itemSizeKey =
          getClimbingBeltSizeKey(searchable);

        return itemSizeKey === sizeKey;
      });

    if (!candidates.length) {
      return null;
    }

    const matchingItem =
      candidates.find(function(item) {
        return /20192cm/i.test(
          `${item.product_title || ''} ${item.handle || ''}`
        );
      }) ||
      candidates.find(function(item) {
        return /climbing\s*belt|body\s*belt|belt/i.test(
          `${item.product_title || ''} ${item.handle || ''}`
        );
      });

    if (!matchingItem) {
      return null;
    }

    const product =
      await fetchProduct(matchingItem.handle);

    if (!product) {
      return null;
    }

    let imageSrc = null;

    if (Array.isArray(product.variants)) {
      const matchedVariant =
        product.variants.find(function(variant) {
          return (
            String(variant.id) ===
            String(matchingItem.variant_id)
          );
        });

      if (
        matchedVariant &&
        matchedVariant.featured_image
      ) {
        imageSrc =
          matchedVariant.featured_image.src;
      }
    }

    if (!imageSrc && product.featured_image) {
      imageSrc = product.featured_image;
    }

    if (
      !imageSrc &&
      Array.isArray(product.images) &&
      product.images.length
    ) {
      imageSrc = product.images[0];
    }

    if (!imageSrc) {
      return null;
    }

    const root =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    let productUrl =
      root +
      'products/' +
      matchingItem.handle;

    if (matchingItem.variant_id) {
      productUrl +=
        '?variant=' +
        encodeURIComponent(
          matchingItem.variant_id
        );
    }

    return {
      imageSrc: resizeImage(
        imageSrc,
        1024
      ),
      productUrl: productUrl
    };
  } catch (error) {
    console.warn(
      '[SB-IMG] Belt preview lookup failed:',
      error
    );

    return null;
  }
}

async function handleChange(select) {
  const selected = select.options[select.selectedIndex];

  /*
   * Each dropdown gets its own clickable preview.
   */
  let preview = select.__titanOptionPreview;

  if (!preview) {
    preview = document.createElement('a');

    preview.className = 'titan-option-preview';
    preview.target = '_blank';
preview.rel = 'noopener';

    preview.innerHTML = `
      <div class="titan-option-preview__image">
        <img src="" alt="">
      </div>

      <div class="titan-option-preview__details">
  <div class="titan-option-preview__link">
    View Product →
  </div>

  <div class="titan-option-preview__preferred" style="display:none;">
    ★ Titan Preferred
  </div>
</div>
    `;

    preview.setAttribute(
      'aria-label',
      'View selected product'
    );

    select.insertAdjacentElement(
      'afterend',
      preview
    );

    select.__titanOptionPreview = preview;
  }

  /*
   * No selection / placeholder.
   */
  if (!selected || !selected.value) {
    preview.style.display = 'none';
    preview.removeAttribute('href');
    return;
  }

  const raw =
  selected.textContent || '';

const normalized =
  normalizeOptionText(raw);

  console.log('[SB-IMG] Dropdown changed:', {
    raw: raw.trim(),
    normalized: normalized
  });

  const decisionCategory =
    getTitanDecisionLabel(select)
      .toLowerCase();

  const isClimbingBelt =
    decisionCategory.includes(
      'climbing belt'
    );

  let imageSrc = null;
  let beltPreviewData = null;

  if (isClimbingBelt) {
    /*
     * Belt-only lookup. Do NOT use the global partial matcher here.
     */
    beltPreviewData =
      await getClimbingBeltPreviewData(raw);

    if (beltPreviewData) {
      imageSrc =
        beltPreviewData.imageSrc;
    }
  } else {
    /*
     * Original, known-good behavior for every other dropdown.
     */
    imageSrc = imageMap[normalized];

    if (!imageSrc) {
      const partialMatch =
        Object.keys(imageMap).find(
          (key) =>
            normalized.includes(key) ||
            key.includes(normalized)
        );

      if (partialMatch) {
        imageSrc =
          imageMap[partialMatch];
      }
    }
  }

  /*
   * Do not include / no mapped product.
   */
  if (!imageSrc) {
    preview.style.display = 'none';
    preview.removeAttribute('href');
    return;
  }

  const cleanProductName = raw
    .replace(
      /\([\+\-]?\s*[A-Z]{0,3}\$?\s*[\d,.]+\)/g,
      ''
    )
    .replace(
      /^(Essential|Premium|Standard)\s*[–-]\s*/i,
      ''
    )
    .trim();
const isTitanPreferred =
  isTitanPreferredSelection(
    select,
    cleanProductName
  );
  const img = preview.querySelector(
    '.titan-option-preview__image img'
  );
const preferredBadge =
  preview.querySelector(
    '.titan-option-preview__preferred'
  );
  img.src = imageSrc;
  img.alt = cleanProductName;

  /*
   * Link to actual child product.
   */
  const productUrl =
    beltPreviewData &&
    beltPreviewData.productUrl
      ? beltPreviewData.productUrl
      : getProductUrlForSelection(
          normalized
        );

  if (productUrl) {
    preview.href = productUrl;

    preview.setAttribute(
      'aria-label',
      'View ' + cleanProductName
    );
  } else {
    preview.removeAttribute('href');

    preview.setAttribute(
      'aria-label',
      cleanProductName
    );
  }
if (preferredBadge) {
  preferredBadge.style.display =
    isTitanPreferred
      ? 'inline-flex'
      : 'none';
}
  preview.style.display = 'flex';
}
function wrapDecisionBox(select) {
  if (select.closest('.titan-decision-box')) {
    return;
  }

  const box = document.createElement('div');
  box.className = 'titan-decision-box';

  /*
   * Find the label connected to this dropdown.
   */
  let label = null;

  if (select.id) {
    label = document.querySelector(
      'label[for="' + CSS.escape(select.id) + '"]'
    );
  }

  /*
   * Simple Bundles fallback:
   * usually the heading/label is immediately before the select.
   */
  if (!label) {
    let previous = select.previousElementSibling;

    while (previous) {
      if (
        previous.tagName === 'LABEL' ||
        previous.matches('strong, .label, .form__label')
      ) {
        label = previous;
        break;
      }

      previous = previous.previousElementSibling;
    }
  }

  /*
   * Put the box where the label currently begins,
   * otherwise where the select currently sits.
   */
  const insertPoint = label || select;

  insertPoint.parentNode.insertBefore(
    box,
    insertPoint
  );

  if (label) {
    box.appendChild(label);
  }

  box.appendChild(select);
}
function addTitanBuilderStartInstruction(
  container
) {

  if (
    container.querySelector(
      '.titan-builder-start'
    )
  ) {
    return;
  }


  const firstDecisionBox =
    container.querySelector(
      '.titan-decision-box'
    );


  if (!firstDecisionBox) {
    return;
  }


  const instruction =
    document.createElement('div');


  instruction.className =
    'titan-builder-start';


  instruction.innerHTML = `
    <div class="titan-builder-start__title">
      START HERE ↓
    </div>

    <div class="titan-builder-start__text">
      Choose from the first dropdown below, then work down the page one section at a time.
    </div>
  `;


  firstDecisionBox.insertAdjacentElement(
    'beforebegin',
    instruction
  );

}
function attachListeners(container) {
  const selects = container.querySelectorAll('select');

  selects.forEach((sel) => {

  wrapDecisionBox(sel);

  addTitanBuilderStartInstruction(
    container
  );

  if (!sel.dataset.titanPreferredValue) {
    sel.dataset.titanPreferredValue =
      sel.value;
  }
  if (sel.dataset.sbImgBound) return;

    sel.dataset.sbImgBound = '1';

    sel.addEventListener('change', () => handleChange(sel));

    // Show current default selection immediately
    handleChange(sel);

    console.log(
      '[SB-IMG] Listener attached:',
      sel.name || sel.id || '(unnamed)'
    );
  });
}

  // ========================================================================
  // INIT
  // ========================================================================

  async function init() {
    // 1. Find main image
    mainImageEl = findMainImage();
    if (!mainImageEl) {
      console.warn('[SB-IMG] Main product image not found. Retrying in 1s...');
      setTimeout(() => {
        mainImageEl = findMainImage();
        if (mainImageEl) {
          console.log('[SB-IMG] Main image found on retry.');
          originalSrc = mainImageEl.src;
          originalSrcset = mainImageEl.getAttribute('srcset');
        } else {
          console.error('[SB-IMG] Could not find main product image.');
        }
      }, 1000);
    } else {
      originalSrc = mainImageEl.src;
      originalSrcset = mainImageEl.getAttribute('srcset');
      console.log('[SB-IMG] Main image found:', originalSrc);
    }

    // 2. Get bundled_variants and build image map
    const bundledVariants = await getBundledVariants();

    if (bundledVariants) {
      loadedBundledVariants = bundledVariants;

      console.log('[SB-IMG] bundled_variants loaded:', bundledVariants.length, 'entries');
      await buildImageMap(bundledVariants);
    } else {
      console.warn(
        '[SB-IMG] Could not load bundled_variants from metafields.',
        'Add the Liquid snippet (see below) for guaranteed compatibility.',
        '\n\nAdd this to your product template:\n',
        '<script>',
        '  window.__SB_BUNDLED_VARIANTS__ = {{ product.metafields.simple_bundles.bundled_variants.value | json }};',
        '</script>'
      );

      // Even without metafield data, try the AJAX handle-guessing approach as fallback
      console.log('[SB-IMG] Falling back to AJAX handle lookup...');
    }

    // 3. Attach to existing dropdowns
    const ioContainer = document.querySelector(CONFIG.ioBundleContainer);
    if (ioContainer) attachListeners(ioContainer);

    // 4. Watch for dynamically injected dropdowns
    const observer = new MutationObserver((mutations) => {
      for (const mut of mutations) {
        for (const node of mut.addedNodes) {
          if (node.nodeType !== 1) continue;
          const container =
            node.matches && node.matches(CONFIG.ioBundleContainer)
              ? node
              : node.querySelector
                ? node.querySelector(CONFIG.ioBundleContainer)
                : null;
          if (container) attachListeners(container);
          if (node.tagName === 'SELECT') {
            const parent = node.closest(CONFIG.ioBundleContainer);
            if (parent) attachListeners(parent);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    console.log('[SB-IMG] Initialized.');
  }

  // Start
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
const titanPreviewStyles = document.createElement('style');

titanPreviewStyles.textContent = `
  .titan-builder-start {
  margin: 0 0 14px;
  padding: 10px 12px;

  border-left: 3px solid #12386f;
  border-radius: 6px;

  background: rgba(18, 56, 111, 0.06);
}

.titan-builder-start__title {
  margin-bottom: 3px;

  color: #12386f;

  font-size: 12px;
  font-weight: 800;
  letter-spacing: .05em;
}

.titan-builder-start__text {
  color: #46577f;

  font-size: 13px;
  line-height: 1.4;
}.titan-option-preview__preferred {
  display: inline-flex;
  align-items: center;

  width: fit-content;

  margin-top: 6px;
  padding: 4px 8px;

  border-radius: 999px;

  background: rgba(18, 56, 111, 0.10);
  color: #12386f;

  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: .02em;
}
  .titan-option-preview {
    display: none;
    align-items: center;
    gap: 12px;

    margin-top: 8px;
    margin-bottom: 10px;
    padding: 9px 10px;

    border: 1px solid rgba(18, 56, 111, 0.16);
    border-radius: 8px;

    background: #fff;

    text-decoration: none;
    color: inherit;
    cursor: pointer;

    transition: border-color .15s ease, background-color .15s ease;
  }

  .titan-option-preview:hover {
    border-color: rgba(18, 56, 111, 0.45);
    background: #f8fafc;
  }

  .titan-option-preview:hover .titan-option-preview__text {
    text-decoration: underline;
  }

  .titan-option-preview__image {
    width: 76px;
    height: 76px;
    min-width: 76px;

    display: flex;
    align-items: center;
    justify-content: center;

    overflow: hidden;

    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 6px;
  }

  .titan-option-preview__image img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .titan-option-preview__text {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    color: #172554;
  }
    .titan-decision-box {
  margin-bottom: 16px;
  padding: 16px;

  border: 1px solid rgba(18, 56, 111, 0.18);
  border-radius: 10px;

  background: #fff;
}

.titan-decision-box > label,
.titan-decision-box > strong,
.titan-decision-box > .label,
.titan-decision-box > .form__label {
  display: block;

  margin: 0 0 8px;

  color: #46577f;

  font-size: 17px;
  font-weight: 700;
}

.titan-decision-box > select {
  width: 100%;
  margin: 0;
}

.titan-decision-box .titan-option-preview {
  margin-top: 10px;
  margin-bottom: 0;

  border: 0;
  border-top: 1px solid rgba(18, 56, 111, 0.12);
  border-radius: 0;

  padding: 10px 0 0;

  background: transparent;
}

.titan-decision-box .titan-option-preview:hover {
  background: transparent;
  border-color: rgba(18, 56, 111, 0.25);
}

@media screen and (max-width: 749px) {
  .titan-decision-box {
    padding: 13px;
    margin-bottom: 13px;
  }
}
.titan-option-preview__details {
  flex: 1;
  min-width: 0;
}

.titan-option-preview__link {
  margin-top: 4px;

  font-size: 12px;
  font-weight: 600;

  color: #040608ff;
}

.titan-option-preview:hover .titan-option-preview__link {
  text-decoration: underline;
}
  @media screen and (max-width: 749px) {
    .titan-option-preview__image {
      width: 64px;
      height: 64px;
      min-width: 64px;
    }
  }
`;

document.head.appendChild(titanPreviewStyles);
})();